import React, {useEffect, useState} from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {RotateCcw, Save} from "lucide-react";
import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {getAuthToken, getClientIdOfUser, showToast} from "../../utils/utility";
import LockShuttle from "./LockShuttle";
import Button from "../../components/ui/Button";
import TextField from "../../components/ui/TextField";
import Select from "../../components/ui/Select";
import Card from "../../components/ui/Card";
import {cn} from "../../utils/cn";

const TRIP_TYPES = [
  {value: "EXAM_PAPER",        label: "Exam Paper"},
  {value: "CASH_IN_TRANSIT",   label: "Cash In Transit"},
  {value: "ELECTION_MATERIAL", label: "Election Material"},
  {value: "GENERAL",           label: "General"},
];

const EMPTY_FORM = {
  name: "",
  tripType: "GENERAL",
  scheduledStart: null,
  scheduledEnd: null,
  description: "",
};

const CreateTrip = ({tripToEdit, setTripToEdit, onSaved}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [assignedIds, setAssignedIds] = useState([]);
  const [allLocks, setAllLocks] = useState([]);
  const [locksLoading, setLocksLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isEdit = !!tripToEdit?.id;

  useEffect(() => {
    const clientId = getClientIdOfUser();
    const base = `${Constants.BASE_URL}${Constants.API_V1}/${clientId}`;
    setLocksLoading(true);
    Promise.all([
      fetch(`${base}/assets/${UrlConstants.GET_ALL_ASSETS}`, {
        headers: {Authorization: `${Constants.BEARER} ${getAuthToken()}`},
      }).then((r) => r.json()),
      fetch(`${base}/${UrlConstants.TRIPS}?page=0&size=500`, {
        headers: {Authorization: `${Constants.BEARER} ${getAuthToken()}`},
      }).then((r) => r.json()),
    ])
      .then(([assetsData, tripsData]) => {
        const gpsLocks = (assetsData.responseData || []).filter(
          (a) => (a.assetType || "").toUpperCase() === "GPS_LOCK"
        );
        const allTrips = tripsData.responseData?.content || tripsData.responseData || [];
        const activeDeviceIds = new Set(
          allTrips
            .filter((t) => (t.status === "ACTIVE" || t.status === "SCHEDULED") && t.id !== tripToEdit?.id)
            .flatMap((t) => t.deviceIds || [])
        );
        setAllLocks(gpsLocks.filter((a) => !activeDeviceIds.has(a.assetUnitId)));
      })
      .catch(() => showToast("Failed to load GPS locks", {type: "error"}))
      .finally(() => setLocksLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tripToEdit?.id) {
      setForm({
        name:           tripToEdit.name           || "",
        tripType:       tripToEdit.tripType       || "GENERAL",
        scheduledStart: tripToEdit.scheduledStart ? new Date(tripToEdit.scheduledStart) : null,
        scheduledEnd:   tripToEdit.scheduledEnd   ? new Date(tripToEdit.scheduledEnd)   : null,
        description:    tripToEdit.description    || "",
      });
      setAssignedIds(tripToEdit.deviceIds || []);
    } else {
      setForm(EMPTY_FORM);
      setAssignedIds([]);
    }
  }, [tripToEdit]);

  const handleChange = (e) => {
    const {name, value} = e.target;
    setForm((prev) => ({...prev, [name]: value}));
  };

  const handleReset = () => {
    setForm(EMPTY_FORM);
    setAssignedIds([]);
    if (setTripToEdit) setTripToEdit(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) { showToast("Fleet trip name is required", {type: "error"}); return; }
    if (!form.scheduledStart || !form.scheduledEnd) { showToast("Scheduled start and end are required", {type: "error"}); return; }
    if (form.scheduledEnd <= form.scheduledStart) { showToast("Scheduled end must be after start", {type: "error"}); return; }

    setSaving(true);
    const clientId = getClientIdOfUser();
    const base = `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/${UrlConstants.TRIPS}`;
    const url = isEdit ? `${base}/${tripToEdit.id}` : base;
    const method = isEdit ? "PUT" : "POST";

    fetch(url, {
      method,
      headers: {"Content-Type": "application/json", Authorization: `${Constants.BEARER} ${getAuthToken()}`},
      body: JSON.stringify({
        requestBody: {
          name:           form.name.trim(),
          tripType:       form.tripType,
          scheduledStart: form.scheduledStart.toISOString(),
          scheduledEnd:   form.scheduledEnd.toISOString(),
          description:    form.description.trim(),
          deviceIds:      assignedIds,
        },
      }),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((d) => { throw new Error(d.responseMessage || res.statusText); });
        return res.json();
      })
      .then(() => {
        showToast(isEdit ? "Fleet trip updated" : "Fleet trip created", {type: "success"});
        handleReset();
        if (onSaved) onSaved();
      })
      .catch((err) => showToast(err.message || (isEdit ? "Failed to update fleet trip" : "Failed to create fleet trip"), {type: "error"}))
      .finally(() => setSaving(false));
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-[1100px] mx-auto space-y-5 pb-8">
      <Card>
        <div className="p-6 border-b border-ink-100">
          <h2 className="text-base font-semibold text-ink-900">
            {isEdit ? `Edit fleet trip · ${tripToEdit.name}` : "New fleet trip"}
          </h2>
          <p className="text-sm text-ink-500 mt-1">
            Define the trip's name, type, schedule and which GPS locks are part of it.
          </p>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <TextField
            label="Trip name"
            required
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="e.g. Chennai exam paper run"
            maxLength={120}
          />
          <div className="space-y-1">
            <label className="block text-sm font-medium text-ink-700">
              Trip type <span className="text-danger-600">*</span>
            </label>
            <Select name="tripType" value={form.tripType} onChange={handleChange}>
              {TRIP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-ink-700">
              Scheduled start <span className="text-danger-600">*</span>
            </label>
            <DatePicker
              selected={form.scheduledStart}
              onChange={(date) => setForm((prev) => ({...prev, scheduledStart: date}))}
              showTimeSelect timeFormat="HH:mm" timeIntervals={15}
              dateFormat="dd/MM/yyyy HH:mm" placeholderText="Select date & time"
              wrapperClassName="w-full"
              className={cn(
                "block w-full h-9 px-3 text-base rounded-md bg-white border border-ink-200",
                "shadow-inset text-ink-900 placeholder:text-ink-400",
                "hover:border-ink-300 focus:border-brand-500 focus:outline-none focus:ring"
              )}
              shouldCloseOnSelect
            />
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-ink-700">
              Scheduled end <span className="text-danger-600">*</span>
            </label>
            <DatePicker
              selected={form.scheduledEnd}
              onChange={(date) => setForm((prev) => ({...prev, scheduledEnd: date}))}
              showTimeSelect timeFormat="HH:mm" timeIntervals={15}
              dateFormat="dd/MM/yyyy HH:mm" placeholderText="Select date & time"
              minDate={form.scheduledStart}
              wrapperClassName="w-full"
              className={cn(
                "block w-full h-9 px-3 text-base rounded-md bg-white border border-ink-200",
                "shadow-inset text-ink-900 placeholder:text-ink-400",
                "hover:border-ink-300 focus:border-brand-500 focus:outline-none focus:ring"
              )}
              shouldCloseOnSelect
            />
          </div>

          <div className="sm:col-span-2 space-y-1">
            <label className="block text-sm font-medium text-ink-700">Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Optional notes about this trip"
              rows={3}
              className={cn(
                "block w-full px-3 py-2 text-base rounded-md bg-white border border-ink-200",
                "shadow-inset text-ink-900 placeholder:text-ink-400 resize-y",
                "hover:border-ink-300 focus:border-brand-500 focus:outline-none focus:ring"
              )}
            />
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-6 border-b border-ink-100">
          <h2 className="text-base font-semibold text-ink-900">GPS Lock assignment</h2>
          <p className="text-sm text-ink-500 mt-1">
            Pick which devices are part of this trip. Locks already on another active trip are hidden.
          </p>
        </div>
        <div className="p-2 sm:p-4 legacy-in-slideover">
          <LockShuttle
            allLocks={allLocks}
            assigned={assignedIds}
            onChange={setAssignedIds}
            disabled={saving}
            loading={locksLoading}
          />
        </div>
      </Card>

      <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" icon={RotateCcw} disabled={saving} onClick={handleReset}>
          Reset
        </Button>
        <Button type="submit" variant="primary" icon={Save} loading={saving}>
          {isEdit ? "Update fleet trip" : "Create fleet trip"}
        </Button>
      </div>
    </form>
  );
};

export default CreateTrip;
